import type { AutomationEvidence } from "../types";

/**
 * Why automation is needed, as a quantity rather than a percentage.
 *
 * A review rate of 82% is a number nobody feels. The same figure drawn as 82 of 100 dots,
 * next to a bar showing how much a team can actually clear, is a number anyone feels.
 * Every value here is derived from the evaluation artifact - the dots are a rendering of
 * `review_rate`, not an illustration drawn to look convincing.
 *
 * One frame throughout (user feedback, 2026-08-15): the dots, the capacity bars and the
 * callout all speak in "per 100 transactions", so the reader never switches units
 * mid-thought. The daily assumptions (100k transactions / 2k review capacity) are not
 * dropped - they follow in their own line, and the ratio is identical either way.
 */

const TOTAL_DOTS = 100;

interface Props {
  evidence: AutomationEvidence;
}

function Dots({ reviewed, total }: { reviewed: number; total: number }) {
  return (
    <div className="dot-grid" aria-hidden="true">
      {Array.from({ length: total }, (_, i) => (
        <span key={i} className={`dot ${i < reviewed ? "dot-review" : "dot-approve"}`} />
      ))}
    </div>
  );
}

export function AutomationScale({ evidence }: Props) {
  const { rule, assumed_daily_transactions: daily, assumed_daily_review_capacity: capacity } =
    evidence;

  // The rule scorer is what actually serves traffic, so the operational picture is its
  // picture. Showing the ML numbers here would describe a system that is not running.
  const reviewedDots = Math.round(rule.review_rate * TOTAL_DOTS);
  const approvedDots = TOTAL_DOTS - reviewedDots;
  const dailyReviews = rule.projected_daily_reviews;
  const timesOverCapacity = dailyReviews / capacity;
  // The capacity assumption is a daily figure; rescaled to the same 100-transaction frame
  // as the dots above so the reader never has to switch units mid-thought. The ratio is
  // unchanged - it is still capacity vs. review load - and the daily figures follow in
  // their own line, so nothing is hidden by the rescaling.
  const capacityPer100 = (capacity / daily) * TOTAL_DOTS;

  return (
    <section className="card">
      <h3>🤔 사람이 다 보면 안 되나요?</h3>
      <p className="muted">
        이 시스템에 거래 100건이 들어오면 실제로 어떻게 나뉘는지를 점으로 그렸어요. 고정된 합성
        테스트셋 평가 결과(현재 서빙 중인 Rule 기준)를 그대로 옮긴 그림이고, 실시간 트래픽이
        아닙니다.
      </p>

      <Dots reviewed={reviewedDots} total={TOTAL_DOTS} />

      <div className="dot-legend">
        <span>
          <span className="dot dot-approve" /> 자동 통과 <strong>{approvedDots}건</strong> — 사람이
          안 봐도 됨
        </span>
        <span>
          <span className="dot dot-review" /> 사람이 검토 <strong>{reviewedDots}건</strong> — 이건
          전부 사람이 하나하나 봐야 해요
        </span>
      </div>

      <h4>사람이 하루에 볼 수 있는 양과 비교하면</h4>
      <p className="muted">
        아래 검토 한계는 <strong>가정치</strong>예요. 측정된 운영 수치가 아니라, 비율을 일의
        양으로 바꿔 실감해 보기 위한 값입니다. 위 그림과 같은 <strong>100건 기준</strong>으로
        환산했어요.
      </p>

      <div className="capacity-chart">
        <div className="capacity-row">
          <span className="capacity-label">사람에게 넘어가는 양</span>
          <span className="capacity-bar over" style={{ width: "100%" }}>
            {reviewedDots}건
          </span>
        </div>
        <div className="capacity-row">
          <span className="capacity-label">사람이 볼 수 있는 양</span>
          <span
            className="capacity-bar limit"
            style={{ width: `${Math.max((capacityPer100 / reviewedDots) * 100, 2)}%` }}
          >
            {capacityPer100.toFixed(0)}건
          </span>
        </div>
      </div>

      <p className="callout">
        거래 <strong>100건</strong>이 들어오면 <strong>{reviewedDots}건</strong>이 사람에게
        넘어가요. 그런데 같은 시간에 사람이 볼 수 있는 건{" "}
        <strong>{capacityPer100.toFixed(0)}건</strong>뿐입니다 —{" "}
        <strong>{timesOverCapacity.toFixed(0)}배 부족</strong>. 숙제가 매일{" "}
        {timesOverCapacity.toFixed(0)}배씩 밀리는 셈이라, 사람을 더 뽑는 걸로는 해결이 안 돼요.
      </p>
      <p className="muted">
        하루 {daily.toLocaleString()}건 규모로 키우면 {dailyReviews.toLocaleString()}건 대{" "}
        {capacity.toLocaleString()}건 — 배수는 똑같이 {timesOverCapacity.toFixed(0)}배입니다.
      </p>

      <h4>그래서 진짜 질문은 이거예요</h4>
      <p>
        &quot;사람이 볼 수 있는 양이 정해져 있다면, 그 양으로 사기를{" "}
        <strong>얼마나 많이</strong> 잡을 수 있을까?&quot;
      </p>
      <table className="evidence-table">
        <thead>
          <tr>
            <th>사람이 전체의 10%만 볼 수 있다고 하면</th>
            <th>잡아내는 사기 비율</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>규칙 방식 (지금 돌아가는 것)</td>
            <td>{(rule.recall_at_10pct_review * 100).toFixed(1)}%</td>
          </tr>
          <tr>
            <td>학습한 AI 모델 (실험)</td>
            <td>
              <strong>{(evidence.ml.recall_at_10pct_review * 100).toFixed(1)}%</strong>
            </td>
          </tr>
        </tbody>
      </table>
      <p className="muted">
        같은 인원으로 사기를 더 잡아요. 그런데도 AI 모델을 투입하지 않았습니다 — 이 프로젝트가
        실험 <strong>전에</strong> 정해둔 합격 기준이 따로 있었고, 그 기준에서는 통과하지 못했기
        때문이에요. &quot;결과를 보고 나서 유리한 기준으로 바꿔치기하지 않는다&quot;가 이 프로젝트의
        규칙입니다. 자세한 근거는 아래에 있어요.
      </p>
    </section>
  );
}
